import { cn } from '@autho/ui';
import { Link, useRouterState } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useClarify, useConnections } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { useT } from '../lib/preferences.tsx';
import { useTenant } from '../lib/tenant.tsx';
import { CommandPalette } from './CommandPalette.tsx';
import { InViewNote } from './InViewNote.tsx';
import type { MenuLink } from './menu.ts';
import { isGroup, menuFor } from './menu.ts';
import { ProfileSwitcher } from './ProfileSwitcher.tsx';
import { ProvenanceDrawer } from './ProvenanceDrawer.tsx';
import { SyncStatus } from '@autho/ui';

/**
 * Design Principles layout: the switcher pinned top-left, nav, top bar, content, right drawer. The switcher is the
 * profiles' since F356 (Thomas, 2026-10-11); which company a screen shows is said on the screen (`InViewNote`).
 *
 * **Below 768 pixels the menu is a rail of icons** (Design Principles: *left nav collapses to icons*). At a phone's
 * 390 the full menu took 240 of them and left a screen about 150 to say anything in (the walk at phone width, 16
 * October). Every word stays in the page for a screen reader and comes back as a tooltip; only its width goes.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const t = useT();
  const { tenantId, kind, chatOpen } = useTenant();
  const { signOut } = useAuth();
  const clarify = useClarify(tenantId);
  const connections = useConnections(tenantId);
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const brreg = connections.data?.find((c) => c.provider === 'brreg')?.connection;
  const openCount = clarify.data?.openCount ?? 0;

  /** One renderer for a top-level entry and for a group's child, so the two cannot drift apart. */
  const link = ({ to, label, icon: Icon }: MenuLink) => {
    const active = path === to || path.startsWith(`${to}/`);
    return (
      <Link
        to={to}
        search={(prev) => prev}
        title={t(label)}
        className={cn(
          'relative flex h-8 items-center justify-center gap-2 rounded px-2 text-sm focus-visible:outline-2 focus-visible:outline-accent md:justify-start',
          active ? 'bg-hover font-medium text-text' : 'text-muted hover:bg-hover hover:text-text',
        )}
        aria-current={active ? 'page' : undefined}
      >
        <Icon aria-hidden className="size-4 shrink-0" />
        <span className="sr-only flex-1 md:not-sr-only">{t(label)}</span>
        {/* Kept on the rail too, over the icon's corner: a count of open questions is the menu's one live figure. */}
        {to === '/clarify' && openCount > 0 && (
          <span className="absolute -top-1 right-0 rounded-full bg-warning/15 px-1.5 text-xs font-medium tabular-nums text-warning md:static">
            {openCount}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div className="grid h-full grid-cols-[3.5rem_1fr] grid-rows-[3rem_1fr] md:grid-cols-[15rem_1fr]">
      {/* **One switcher, and it lists profiles only** (F356; Thomas, 2026-10-11: Personal first, then the company
          profiles; a company held inside a profile is not a profile, and is reached through its pages). It sits
          top-left, where the entity picker was, and the whole top row is the banner, so the list it opens is in a
          landmark like everything else a person reads. There is no second switcher to disagree with it. */}
      <header className="col-span-2 flex border-b border-border bg-surface">
        <div className="flex w-14 shrink-0 items-center border-r border-border px-2 md:w-60">
          <ProfileSwitcher />
        </div>
        <div className="flex min-w-0 flex-1 items-center justify-end gap-3 px-4">
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="inline-flex h-7 items-center gap-2 rounded border border-border px-2 text-xs text-muted hover:bg-hover focus-visible:outline-2 focus-visible:outline-accent"
          >
            <Search aria-hidden className="size-3.5" />
            <span className="sr-only md:not-sr-only">{t('palette.placeholder')}</span>
            <kbd className="hidden rounded border border-border px-1 text-[10px] md:inline">
              {t('palette.shortcut')}
            </kbd>
          </button>
          {brreg && (
            <SyncStatus
              compact
              status={brreg.status}
              lastSuccessAt={brreg.lastSuccessAt}
              lastAttemptAt={brreg.lastAttemptAt}
            />
          )}
          <button
            type="button"
            onClick={() => void signOut()}
            className="rounded px-2 py-1 text-xs text-muted hover:bg-hover hover:text-text focus-visible:outline-2 focus-visible:outline-accent"
          >
            {t('auth.signOut')}
          </button>
        </div>
      </header>

      {/* One renderer for both a top-level entry and a group's child, so they cannot drift apart. Each profile
          has its own menu (R4), and the landmark is named by the profile's kind, so a screen reader's list of
          landmarks says whose menu it is. */}
      <nav
        aria-label={t(kind === 'me' ? 'tenant.kind.me' : 'tenant.kind.org')}
        className="overflow-y-auto border-r border-border bg-surface p-2"
      >
        <ul className="flex flex-col gap-0.5">
          {menuFor(kind, { chat: chatOpen }).map((entry) =>
            isGroup(entry) ? (
              <li key={entry.label}>
                {/* On the rail a group is a gap between icons, and its name stays for a screen reader. The words are
                    hidden, not the heading: `not-sr-only` also clears padding, so on the heading itself it took the
                    space above the group away at full width. */}
                <h2 className="px-2 pb-1 pt-3 text-xs font-medium uppercase tracking-wide text-muted">
                  <span className="sr-only md:not-sr-only">{t(entry.label)}</span>
                </h2>
                <ul className="flex flex-col gap-0.5">
                  {entry.items.map((item) => (
                    <li key={item.to}>{link(item)}</li>
                  ))}
                </ul>
              </li>
            ) : (
              // A module with one screen needs no heading, but it does need to look like its own module: with
              // no space above it, Beliefs read as the last item of Mind (seen in the screenshot, not the test).
              <li key={entry.to} className="pt-3 first:pt-0">
                {link(entry)}
              </li>
            ),
          )}
        </ul>
      </nav>

      <main className="min-w-0 overflow-y-auto">
        <InViewNote />
        {children}
      </main>

      <ProvenanceDrawer />
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
